import http from "./http";
import api from "./api";

/**
 * Fetch EBM settings for a store (admin endpoint)
 * @param {number} storeId - The store ID
 * @returns {Promise<Object>} EBM settings object
 */
export async function getStoreEbmSettings(storeId) {
  try {
    const response = await http.get(api.admin_ebm_store_settings(storeId));
    return response.data;
  } catch (error) {
    console.error("Failed to fetch store EBM settings", error);
    throw error;
  }
}

/**
 * Force re-initialize EBM for a store
 * @param {number} storeId - The store ID
 * @param {string} tin - The TIN number
 * @param {string} bhfId - The branch ID
 * @param {boolean} force - Whether to force re-initialization (bypass check)
 * @returns {Promise<Object>} Response from the initialization endpoint
 */
export async function initializeEbm(storeId, tin, bhfId, force = false) {
  try {
    const payload = {
      store_id: storeId,
      tin: tin,
      bhfId: bhfId,
      force: force,
    };

    const response = await http.post(api.rra_ebm_initializer, payload);
    // Return full response object to preserve status and headers if needed
    return response;
  } catch (error) {
    console.error("Failed to initialize EBM", error);
    throw error;
  }
}

/**
 * Initialize EBM for a store (convenience function)
 * Fetches EBM settings first, then calls initialization
 * @param {number} storeId - The store ID
 * @param {boolean} force - Whether to force re-initialization (bypass check)
 * @returns {Promise<Object>} Response from the initialization endpoint
 */
export async function initializeEbmForStore(storeId, force = false) {
  try {
    // First, fetch the EBM settings to get TIN and bhfId
    const ebmSettings = await getStoreEbmSettings(storeId);

    // Handle both camelCase (bhfId) and snake_case (bhf_id) formats
    const tin = ebmSettings?.tin;
    const bhfId = ebmSettings?.bhfId || ebmSettings?.bhf_id;

    if (!tin || !bhfId) {
      throw new Error("Store EBM settings missing TIN or Branch ID");
    }

    // Call initialization with or without force
    return await initializeEbm(
      storeId,
      tin,
      bhfId,
      force
    );
  } catch (error) {
    console.error("Failed to initialize EBM", error);
    throw error;
  }
}

/**
 * Force re-initialize EBM for a store (convenience function)
 * @deprecated Use initializeEbmForStore with force=true instead
 * @param {number} storeId - The store ID
 * @returns {Promise<Object>} Response from the initialization endpoint
 */
export async function forceReinitializeEbm(storeId) {
  return initializeEbmForStore(storeId, true);
}
